<script>
  // Smokescreen analytics page. Pulls PromQL from the hook-index backend
  // (which fans out to both telebroad Prometheus instances) and renders
  // a small fixed dashboard. The set of panels is hard-coded for now —
  // these are the questions you usually want to answer about an egress
  // proxy: throughput, latency, deny/allow split, top destinations,
  // top callers, error volume. Add a new panel by appending to PANELS.
  import { onMount, onDestroy, tick } from 'svelte';
  import { api } from '../lib/api.js';
  import uPlot from 'uplot';
  import 'uplot/dist/uPlot.min.css';

  // Range presets (label, seconds). 1h default — smokescreen is bursty
  // (webhook fan-outs cluster around alert events) so anything longer
  // than ~6h tends to lose the spikes when downsampled.
  const RANGES = [
    { label: '15m', secs: 15 * 60,      step: 15 },
    { label: '1h',  secs: 60 * 60,      step: 30 },
    { label: '6h',  secs: 6 * 60 * 60,  step: 60 },
    { label: '24h', secs: 24 * 60 * 60, step: 300 },
  ];

  // Each panel: human title + raw PromQL. We always use rate(...[5m])
  // for counters so panels show "per second" rather than monotonically-
  // increasing totals — same convention smokescreen's stripe dashboard
  // uses upstream.
  //
  // "Logs" context: smokescreen emits one structured log line per CONNECT
  // decision. The statsd exporter maps those into labelled Prometheus
  // counters so we can approximate "what the logs would tell you" entirely
  // from metrics without shipping log files anywhere.
  const PANELS = [
    {
      id: 'requests',
      title: 'Requests per second',
      help: 'CONNECT decisions made by smokescreen, broken down by allow/deny.',
      expr: 'sum by (allow) (rate(smokescreen_acl_decisions_total[5m]))',
      legend: (s) => s.allow === 'true' ? 'allowed' : 'denied',
      unit: 'req/s',
    },
    {
      id: 'deny_reasons',
      title: 'Deny reasons per second',
      help: 'Why connections were blocked — ACL rule, private IP, bad host, etc. Each series is a distinct deny reason label from smokescreen.',
      expr: 'sum by (reason) (rate(smokescreen_acl_decisions_total{allow="false"}[5m]))',
      legend: (s) => s.reason || 'denied',
      unit: 'req/s',
    },
    {
      id: 'duration_p95',
      title: 'Connection duration p95 (s)',
      help: 'Time from CONNECT start to close, sliding 5m window, p95 — webhook delivery latency including the destination response.',
      expr: 'histogram_quantile(0.95, sum by (le, role) (rate(smokescreen_cn_duration_bucket[5m]) or rate(smokescreen_cn_duration[5m])))',
      legend: (s) => s.role || 'all',
      unit: 's',
    },
    {
      id: 'bytes_out',
      title: 'Bytes out per second',
      help: 'Egress traffic from smokescreen → customer webhook destination, summed across all callers.',
      expr: 'sum by (role) (rate(smokescreen_cn_bytes_out_sum[5m]))',
      legend: (s) => s.role || 'all',
      unit: 'B/s',
    },
    {
      id: 'errors',
      title: 'Errors per second',
      help: 'Failed CONNECT attempts — denied by ACL, IP filter, TLS handshake errors, etc.',
      expr: 'sum by (kind) (rate(smokescreen_cn_atpt_total{success="false"}[5m]) or vector(0))',
      legend: (s) => s.kind || 'unknown',
      unit: 'err/s',
    },
    {
      id: 'active_conns',
      title: 'Active connections',
      help: 'Currently open proxy connections — a sustained spike here means callers are waiting on slow webhook destinations.',
      expr: 'smokescreen_cn_open',
      legend: () => 'open',
      unit: 'conns',
    },
  ];

  let info = $state({ urls: [], configured: false, loading: true, error: '' });
  let activeRange = $state(RANGES[1]);   // 1h default
  let autoRefresh = $state(true);
  let panelsState = $state(Object.fromEntries(PANELS.map((p) => [p.id, { loading: true, error: '', series: [] }])));
  /** @type {Record<string, uPlot|null>} */
  let plots = {};
  /** @type {Record<string, HTMLDivElement|null>} */
  let chartEls = $state({});
  let refreshTimer;
  let abortCtrl;

  onMount(async () => {
    try {
      info = { ...await api.smokescreenInfo(), loading: false, error: '' };
    } catch (e) {
      info = { urls: [], configured: false, loading: false, error: String(e?.message || e) };
    }
    await refreshAll();
    if (autoRefresh) startTimer();
  });

  onDestroy(() => {
    stopTimer();
    abortCtrl?.abort();
    for (const p of Object.values(plots)) p?.destroy();
  });

  function startTimer() {
    stopTimer();
    refreshTimer = setInterval(refreshAll, 30_000);
  }
  function stopTimer() {
    if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = undefined; }
  }

  function setRange(r) {
    activeRange = r;
    refreshAll();
  }

  function toggleAuto() {
    autoRefresh = !autoRefresh;
    if (autoRefresh) startTimer(); else stopTimer();
  }

  async function refreshAll() {
    abortCtrl?.abort();
    abortCtrl = new AbortController();
    const now = Math.floor(Date.now() / 1000);
    const from = now - activeRange.secs;
    await Promise.all(PANELS.map((p) => refreshPanel(p, from, now)));
  }

  async function refreshPanel(panel, from, to) {
    panelsState[panel.id] = { ...panelsState[panel.id], loading: true };
    try {
      const res = await api.smokescreenRange(panel.expr, {
        from,
        to,
        step: activeRange.step,
        signal: abortCtrl.signal,
      });
      const series = parseSeries(res, panel);
      panelsState[panel.id] = { loading: false, error: '', series };
      // Wait a tick so the new chart div mounts before uPlot binds.
      await tick();
      renderChart(panel, series);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      panelsState[panel.id] = { loading: false, error: String(e?.message || e), series: [] };
    }
  }

  // Prometheus matrix result → uPlot data shape ([xs, ys1, ys2, ...]) +
  // labels for the legend. xs is a sorted union of all per-series ts so
  // gaps line up across panels.
  function parseSeries(res, panel) {
    const result = res?.data?.result || [];
    if (!Array.isArray(result) || result.length === 0) return [];
    const xs = new Set();
    const named = result.map((s) => {
      const label = panel.legend(s.metric) || JSON.stringify(s.metric);
      const points = new Map();
      for (const [t, v] of (s.values || [])) {
        const n = Number(v);
        if (!Number.isFinite(n)) continue;
        xs.add(t);
        points.set(t, n);
      }
      return { label, points };
    });
    const xsSorted = [...xs].sort((a, b) => a - b);
    return [xsSorted, ...named.map((s) => xsSorted.map((t) => s.points.has(t) ? s.points.get(t) : null))]
      .concat([named.map((s) => s.label)]);
  }

  function renderChart(panel, series) {
    const el = chartEls[panel.id];
    if (!el) return;
    if (!Array.isArray(series) || series.length < 2) {
      plots[panel.id]?.destroy();
      plots[panel.id] = null;
      return;
    }
    const labels = series[series.length - 1];
    const data = series.slice(0, -1);
    const opts = {
      width: el.clientWidth,
      height: 240,
      cursor: { drag: { x: true, y: false } },
      legend: { live: false },
      scales: { x: { time: true }, y: { auto: true } },
      series: [
        { label: 'time' },
        ...labels.map((label, i) => ({
          label,
          stroke: PALETTE[i % PALETTE.length],
          width: 2,
          spanGaps: false,
        })),
      ],
      axes: [
        { stroke: 'currentColor' },
        { stroke: 'currentColor' },
      ],
    };
    plots[panel.id]?.destroy();
    plots[panel.id] = new uPlot(opts, data, el);
  }

  // M3-leaning colour palette — soft and distinguishable in dark mode.
  const PALETTE = ['#7dd3fc', '#fda4af', '#bef264', '#fcd34d', '#c4b5fd', '#5eead4'];
</script>

<div class="space-y-4">
  <header class="flex items-center justify-between gap-4 flex-wrap">
    <div>
      <p class="text-sm text-on-surface-variant">
        Live metrics from the webhook-egress proxy. Pulled from
        {#if info.urls.length > 0}
          {info.urls.length} Prometheus instance{info.urls.length === 1 ? '' : 's'}
        {:else if info.error}
          (error: {info.error})
        {:else}
          Prometheus
        {/if}.
      </p>
    </div>
    <div class="flex items-center gap-2">
      {#each RANGES as r}
        <button
          onclick={() => setRange(r)}
          class="px-3 py-1.5 rounded-full text-sm border border-outline-variant transition {activeRange.label === r.label ? 'bg-primary-container text-on-primary-container' : 'hover:bg-surface-container-high'}"
        >{r.label}</button>
      {/each}
      <button
        onclick={toggleAuto}
        class="px-3 py-1.5 rounded-full text-sm border border-outline-variant transition {autoRefresh ? 'bg-primary-container text-on-primary-container' : 'hover:bg-surface-container-high'}"
        title="Auto-refresh every 30s"
      >{autoRefresh ? 'Auto: on' : 'Auto: off'}</button>
      <button
        onclick={refreshAll}
        class="px-3 py-1.5 rounded-full text-sm border border-outline-variant hover:bg-surface-container-high transition"
      >Refresh</button>
    </div>
  </header>

  {#if !info.loading && !info.configured}
    <div class="p-4 rounded-lg bg-error-container text-on-error-container text-sm">
      Prometheus is not configured on this hook-index. Set PROMETHEUS_URLS in the deployment env.
    </div>
  {/if}

  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
    {#each PANELS as panel (panel.id)}
      <section class="rounded-2xl border border-outline-variant bg-surface-container-low p-4 flex flex-col gap-2">
        <header class="flex items-baseline justify-between gap-2">
          <h3 class="text-base font-medium">{panel.title}</h3>
          <span class="text-xs text-on-surface-variant">{panel.unit}</span>
        </header>
        <p class="text-xs text-on-surface-variant">{panel.help}</p>
        {#if panelsState[panel.id]?.error}
          <div class="text-xs text-error">Failed to load: {panelsState[panel.id].error}</div>
        {/if}
        <div bind:this={chartEls[panel.id]} class="w-full sk-chart" style="min-height:240px;"></div>
        {#if panelsState[panel.id]?.loading}
          <div class="text-xs text-on-surface-variant">Loading…</div>
        {:else if panelsState[panel.id]?.series.length === 0}
          <div class="text-xs text-on-surface-variant">No data in the selected range.</div>
        {/if}
      </section>
    {/each}
  </div>
</div>

<style>
  /* uPlot's default font sizing is a hair too aggressive for the M3 surfaces. */
  :global(.u-legend) { font-size: 12px; }

  /* uPlot renders a canvas whose sub-pixel height (device-pixel-ratio
     rounding) can sit a pixel or two past the container, producing a
     tiny page scrollbar. line-height:0 kills the inline baseline gap under
     the canvas and overflow:hidden clips any residual fraction. */
  .sk-chart { overflow: hidden; line-height: 0; }
  .sk-chart :global(.uplot) { display: block; }
</style>
